import { WarningItem } from '@/types/warnings'
import datacentral from '@/utils/axios/datacentral'

export interface WarningsResponse {
    message: string
    results: WarningItem[]
}

// Separate API calls for each warning type
export const getContractWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/contracts')
    return response.data
}

const getDayOffWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/dayoffs')
    return response.data
}

const getDocumentRetrievalWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/document-retrieval')
    return response.data
}

const getSubcontractDocumentRetrievalWarnings =
    async (): Promise<WarningsResponse> => {
        const response = await datacentral.get(
            '/warnings/subcontract-document-retrieval'
        )
        return response.data
    }

export const getPOCServiceWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/poc-services')
    return response.data
}

const getTicketWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/tickets')
    return response.data
}

const getServiceWaitingConfirmWarnings =
    async (): Promise<WarningsResponse> => {
        const response = await datacentral.get(
            '/warnings/service-waiting-confirm'
        )
        return response.data
    }

const getServiceMissingUUIDWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/service-missing-uuid')
    return response.data
}

const getLeadWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/leads')
    return response.data
}

// Su kien khach hang sap toi (sinh nhat / ngay thanh lap), cua so hom nay + 3 ngay.
// Backend tu loc theo nguoi goi: BOD/BOM/Leader/Admin thay toan bo, con lai chi thay khach
// minh nam trong tap follower — dung chung ResolveCustomerViewerScope voi tab lich.
const getCustomerEventWarnings = async (): Promise<WarningsResponse> => {
    const response = await datacentral.get('/warnings/customer-events')
    return response.data
}

// Department-based scope mapping.
//
// Values are typed against WarningItem['type'] rather than string. A scope named
// ticket_overdue used to sit in this map while the union never contained it, so
// fetchWarningsForScopes silently discarded every ticket warning; the untyped
// Record is what let that survive.
//
// This catches a scope missing from the union. It cannot catch a name the server
// spells differently, because the two repos share no types — poc_service_upcoming
// here against poc_service_expiring in the API is exactly that, still unfixed.
// Nor are the keys checked: they are department names, not types.
//
// Keys are the lowercased department_name. A department missing from here yields
// an empty scope list, and `enabled: allowedScopes.length > 0` then stops the
// request altogether, so an absent key is not a smaller bell — it is no bell.
const DEPARTMENT_SCOPES: Record<string, WarningItem['type'][]> = {
    // 2. SALES - Sales department focuses on contracts and client relations
    sales: [
        'dayoff_pending',
        'contract_expired',
        'contract_expiring',
        'document_retrieval_upcoming',
        'document_retrieval_overdue',
        'subcontract_document_retrieval_upcoming',
        'subcontract_document_retrieval_overdue',
        'poc_service_overdue',
        'poc_service_upcoming',
        'service_waiting_confirm',
        'lead_unassigned',
        'lead_stale',
        'ticket_escalated',
    ],

    // 3. ACCOUNT - Account management has access to all warnings
    account: [
        'dayoff_pending',
        'contract_expired',
        'contract_expiring',
        'document_retrieval_upcoming',
        'document_retrieval_overdue',
        'subcontract_document_retrieval_upcoming',
        'subcontract_document_retrieval_overdue',
        'poc_service_overdue',
        'poc_service_upcoming',
        'service_waiting_confirm',
        'service_missing_uuid',
        'ticket_escalated',
        'lead_unassigned',
        'lead_stale',
    ],

    // 4. MARKETING - Marketing may need contract info for campaigns
    marketing: [
        'dayoff_pending',
        'lead_unassigned',
        'lead_stale',
        'ticket_escalated',
    ],

    // 5. FINANCE - Finance handles contract renewals and payments
    finance: [
        'dayoff_pending',
        'contract_expired',
        'contract_expiring',
        'document_retrieval_upcoming',
        'document_retrieval_overdue',
        'subcontract_document_retrieval_upcoming',
        'subcontract_document_retrieval_overdue',
        // 2026-07-31: Finance holds Contracts Customers:create on production, so its staff
        // create the service contract and now pass serviceConfirmGate on the API. Without this
        // scope they would hold the power to confirm with no signal that anything is waiting.
        'service_waiting_confirm',
        'lead_unassigned',
        'lead_stale',
        'ticket_escalated',
    ],

    // 7. TECHNOLOGY - Technical team handles services and infrastructure
    technology: [
        'dayoff_pending',
        'service_missing_uuid',
        // Tech already held Services:approved on production before the 2026-07-31 widening —
        // the bell was the part that was missing, not the permission.
        'service_waiting_confirm',
        'ticket_escalated',
        'lead_unassigned',
        'lead_stale',
    ],

    // 8. DEVELOPMENT - Development team has technical warnings
    development: [
        'dayoff_pending',
        'lead_unassigned',
        'lead_stale',
        'ticket_escalated',
    ],

    // 6. HUMAN RESOURCE - HR manages employee day-offs and internal processes
    'human resource': [
        'dayoff_pending',
        'lead_unassigned',
        'lead_stale',
        'ticket_escalated',
    ],

    // 1. BOARD OF DIRECTORS - Leadership has access to all critical warnings
    'board of directors': [
        'contract_expired',
        'contract_expiring',
        'dayoff_pending',
        'document_retrieval_upcoming',
        'document_retrieval_overdue',
        'subcontract_document_retrieval_upcoming',
        'subcontract_document_retrieval_overdue',
        'poc_service_overdue',
        'poc_service_upcoming',
        'service_waiting_confirm',
        'service_missing_uuid',
        'ticket_escalated',
        'lead_unassigned',
        'lead_stale',
    ],

    // 9. SALE PARTNER - Sales partners focus on contracts and opportunities
    'sale partner': ['lead_unassigned', 'lead_stale', 'ticket_escalated'],

    // Departments that existed with no entry at all, so their staff received
    // nothing. They get the escalation scope only: the server already filters
    // escalations to the people answerable for the ticket, whereas granting the
    // other scopes here would be inventing business rules nobody asked for.
    // 'sales partner' is separate from 'sale partner' above — both spellings
    // exist as real departments.
    // 2026-07-31: these three gained the service confirmation route when serviceConfirmGate
    // replaced RequirePermission(Services,[approved]) — 'approved' does not exist as a scope on
    // the staging Keycloak at all, so the route had been Admin-only there. CSKH is the department
    // that actually performs the confirmation, so the bell belongs to it first.
    'customer service': ['service_waiting_confirm', 'ticket_escalated'],
    'finance & accounting': ['service_waiting_confirm', 'ticket_escalated'],
    'board of management': ['service_waiting_confirm', 'ticket_escalated'],
    'solutions 1 (development)': ['ticket_escalated'],
    'solutions 2 (technology)': ['ticket_escalated'],
    'sales partner': ['ticket_escalated'],
    // Plural spelling seeded by the canonical-departments migration. No row
    // carries it on staging today, where the department is HUMAN RESOURCE, but
    // the seed uses ON CONFLICT DO NOTHING so the two spellings can coexist and
    // a missing key costs the whole bell rather than one scope.
    'human resources': [
        'dayoff_pending',
        'lead_unassigned',
        'lead_stale',
        'ticket_escalated',
    ],
}

// API function mapping. Keyed by the union so tsc fails on a scope with no
// endpoint behind it, and on a warning type nobody ever fetches.
type WarningSource =
    | 'contracts'
    | 'dayoffs'
    | 'document-retrieval'
    | 'subcontract-document-retrieval'
    | 'poc-services'
    | 'tickets'
    | 'service-waiting-confirm'
    | 'service-missing-uuid'
    | 'leads'
    | 'customer-events'

interface WarningDescriptor {
    source: WarningSource
    types: WarningItem['type'][]
    fetch: () => Promise<WarningsResponse>
}

export interface WarningsFetchResult {
    warnings: WarningItem[]
    failedSources: WarningSource[]
}

const WARNING_DESCRIPTORS: WarningDescriptor[] = [
    {
        source: 'contracts',
        types: ['contract_expired', 'contract_expiring'],
        fetch: getContractWarnings,
    },
    {
        source: 'document-retrieval',
        types: ['document_retrieval_upcoming', 'document_retrieval_overdue'],
        fetch: getDocumentRetrievalWarnings,
    },
    {
        source: 'subcontract-document-retrieval',
        types: [
            'subcontract_document_retrieval_upcoming',
            'subcontract_document_retrieval_overdue',
        ],
        fetch: getSubcontractDocumentRetrievalWarnings,
    },
    {
        source: 'poc-services',
        types: ['poc_service_overdue', 'poc_service_upcoming'],
        fetch: getPOCServiceWarnings,
    },
    {
        source: 'service-waiting-confirm',
        types: ['service_waiting_confirm'],
        fetch: getServiceWaitingConfirmWarnings,
    },
    {
        source: 'service-missing-uuid',
        types: ['service_missing_uuid'],
        fetch: getServiceMissingUUIDWarnings,
    },
    { source: 'dayoffs', types: ['dayoff_pending'], fetch: getDayOffWarnings },
    {
        source: 'tickets',
        types: ['ticket_escalated'],
        fetch: getTicketWarnings,
    },
    {
        source: 'leads',
        types: ['lead_unassigned', 'lead_stale'],
        fetch: getLeadWarnings,
    },
    {
        source: 'customer-events',
        types: ['customer_event_upcoming'],
        fetch: getCustomerEventWarnings,
    },
]

// Get scopes based on employee department
export const getScopesByDepartment = (
    department?: string
): WarningItem['type'][] => {
    if (!department) return []

    const normalizedDepartment = department.toLowerCase()
    return DEPARTMENT_SCOPES[normalizedDepartment] || []
}

// Priority order for displaying warning types
export const WARNING_TYPE_ORDER: WarningItem['type'][] = [
    // First on purpose: an escalated ticket is someone asking for help right
    // now, where the rest are deadlines approaching.
    'ticket_escalated',
    // Su kien co ngay gio co dinh, khong the doi — xep truoc cac han chot mem hon.
    'customer_event_upcoming',
    'lead_unassigned',
    'lead_stale',
    'contract_expired',
    'contract_expiring',
    'dayoff_pending',
    'service_waiting_confirm',
    'service_missing_uuid',
    'poc_service_overdue',
    'poc_service_upcoming',
    'document_retrieval_overdue',
    'document_retrieval_upcoming',
    'subcontract_document_retrieval_overdue',
    'subcontract_document_retrieval_upcoming',
]

// Fetch warnings for specific scopes
export const fetchWarningsForScopes = async (
    scopes: WarningItem['type'][]
): Promise<WarningsFetchResult> => {
    const descriptors = WARNING_DESCRIPTORS.filter((descriptor) =>
        descriptor.types.some((type) => scopes.includes(type))
    )

    const settled = await Promise.allSettled(
        descriptors.map((descriptor) => descriptor.fetch())
    )
    const failedSources = settled.flatMap((result, index) =>
        result.status === 'rejected' ? [descriptors[index].source] : []
    )

    if (descriptors.length > 0 && failedSources.length === descriptors.length) {
        throw new Error(
            `All warning sources failed: ${failedSources.join(', ')}`
        )
    }

    const warnings = settled.flatMap((result) =>
        result.status === 'fulfilled'
            ? (result.value.results ?? []).filter((item) =>
                  scopes.includes(item.type)
              )
            : []
    )

    return { warnings, failedSources }
}
